
import React, { useState } from 'react';
import { Ruler, MapPin, Hammer, Paintbrush, ArrowRight, ChevronLeft } from 'lucide-react';
import { ProjectType } from '../types';

interface OnboardingStep2Props {
  name: string;
  budget: number;
  onNext: (data: { type: ProjectType; area: number; city: string; state: string }) => void;
  onBack: () => void;
}

export const OnboardingStep2: React.FC<OnboardingStep2Props> = ({ name, budget, onNext, onBack }) => {
  const [formData, setFormData] = useState({
    type: ProjectType.CONSTRUCTION,
    area: '',
    city: '',
    state: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Validação de segurança para evitar NaN
    const areaValue = parseFloat(formData.area);
    onNext({
      type: formData.type,
      area: isNaN(areaValue) ? 0 : areaValue,
      city: formData.city,
      state: formData.state
    });
  };

  const inputClasses = "w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm rounded-2xl py-4 pl-12 pr-4 focus:ring-4 focus:ring-emerald-100 dark:focus:ring-emerald-800/30 transition-all outline-none font-semibold text-slate-900 dark:text-white text-sm";
  const labelClasses = "text-[10px] uppercase font-black text-slate-500 dark:text-slate-400 tracking-widest block mb-3 ml-1";

  return (
    <div className="min-h-screen mesh-gradient flex flex-col px-8 pt-12 pb-10 animate-fadeIn overflow-y-auto">
      {/* Header Info */}
      <div className="bg-emerald-600 text-white p-6 rounded-[2.5rem] mb-8 shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-24 h-24 bg-white/5 rounded-full -mr-10 -mt-10 blur-2xl"></div>
        <div className="relative z-10">
          <p className="text-[10px] font-black uppercase tracking-widest opacity-60 mb-1">{name}</p>
          <p className="text-xl font-bold tracking-tight">R$ {budget.toLocaleString('pt-BR')}</p>
        </div>
      </div>

      <div className="mb-10 flex items-center gap-4">
        <button onClick={onBack} className="p-2 bg-white dark:bg-slate-900 rounded-full shadow-sm hover:bg-slate-50 transition-colors">
          <ChevronLeft size={20} className="text-slate-500" />
        </button>
        <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className="h-full bg-emerald-500 w-2/4 rounded-full transition-all duration-700 ease-out shadow-[0_0_10px_rgba(20,184,75,0.3)]"></div>
        </div>
        <span className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">2 / 4</span>
      </div>

      <h2 className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter mb-8 leading-tight">
        Conte-nos mais sobre a <br/><span className="text-emerald-500">estrutura física</span>
      </h2>

      <form onSubmit={handleSubmit} className="space-y-6 flex-1">
        <div>
          <label className={labelClasses}>Tipo de Obra</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setFormData({...formData, type: ProjectType.CONSTRUCTION})}
              className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${formData.type === ProjectType.CONSTRUCTION ? 'bg-emerald-50 border-emerald-500 dark:bg-emerald-900/20 shadow-lg scale-[1.02]' : 'bg-white border-slate-100 dark:bg-slate-800 dark:border-slate-700'}`}
            >
              <Hammer size={20} className={formData.type === ProjectType.CONSTRUCTION ? 'text-emerald-600' : 'text-slate-400'} />
              <span className={`text-[10px] font-black uppercase tracking-widest ${formData.type === ProjectType.CONSTRUCTION ? 'text-emerald-900 dark:text-white' : 'text-slate-400'}`}>Construção</span>
            </button>
            <button
              type="button"
              onClick={() => setFormData({...formData, type: ProjectType.RENOVATION})}
              className={`flex flex-col items-center gap-2 p-4 rounded-2xl border-2 transition-all ${formData.type === ProjectType.RENOVATION ? 'bg-emerald-50 border-emerald-500 dark:bg-emerald-900/20 shadow-lg scale-[1.02]' : 'bg-white border-slate-100 dark:bg-slate-800 dark:border-slate-700'}`}
            >
              <Paintbrush size={20} className={formData.type === ProjectType.RENOVATION ? 'text-emerald-600' : 'text-slate-400'} />
              <span className={`text-[10px] font-black uppercase tracking-widest ${formData.type === ProjectType.RENOVATION ? 'text-emerald-900 dark:text-white' : 'text-slate-400'}`}>Reforma</span>
            </button>
          </div>
        </div>

        <div>
          <label className={labelClasses}>Área (m²)</label>
          <div className="relative">
            <Ruler className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600/60" size={18} />
            <input 
              type="number" required placeholder="Ex: 150" 
              className={inputClasses}
              value={formData.area}
              onChange={e => setFormData({...formData, area: e.target.value})}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClasses}>Cidade</label>
            <div className="relative">
              <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 text-emerald-600/60" size={18} />
              <input 
                type="text" required placeholder="Ex: Curitiba" 
                className={inputClasses}
                value={formData.city}
                onChange={e => setFormData({...formData, city: e.target.value})}
              />
            </div>
          </div>
          <div>
            <label className={labelClasses}>Estado</label>
            <div className="relative">
              <input 
                type="text" required placeholder="UF" maxLength={2}
                className="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm rounded-2xl py-4 px-4 focus:ring-4 focus:ring-emerald-100 dark:focus:ring-emerald-800/30 transition-all outline-none font-semibold text-slate-900 dark:text-white text-sm text-center uppercase"
                value={formData.state}
                onChange={e => setFormData({...formData, state: e.target.value.toUpperCase()})}
              />
            </div>
          </div>
        </div>

        <div className="pt-6 pb-6">
          <button 
            type="submit"
            className="w-full bg-emerald-600 text-white py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-2xl flex items-center justify-center gap-3 active:scale-95 transition-all hover:bg-emerald-700"
          >
            Continuar
            <ArrowRight size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};